import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import api from '../../../utils/api';
import { ActionButton, IconButton } from '../common/AdminButtons';
import { Table, Tr, Td, Th } from '../common/AdminTable';
import { ErrorMessage, LoadingMessage } from '../common/AdminMessages';
import { Modal } from '../../Modal';
import StyleForm from './StyleForm';
import Style  from '../../../models/Style';

const ListContainer = styled.div`
  padding: 20px;
`;

const StyleList: React.FC = () => {
  const [styles, setStyles] = useState<Style[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState<Style | undefined>(undefined);

  const fetchStyles = async () => {
    try {
      const response = await api.get('/halo/styles/');
      setStyles(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch styles');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStyles();
  }, []);

  const editStyle = useCallback((id: number) => {
    setSelectedStyle(styles.find(item => item.id === id));
    setIsModalOpen(true);
  }, [styles]);

  const addStyle = () => {
    setSelectedStyle(undefined);
    setIsModalOpen(true);
  };

  const deleteStyle = async (id: number, name: string) => {
    if (window.confirm(`Delete the style "${name}"?`)) {
      const response = await api.delete(`/halo/styles/${id}`);
      if (response.status >= 200 && response.status <= 300) {
        fetchStyles();
      } else {
        alert(`HTTP ${response.status}: ${response.statusText}`);
      }
    }
  };

  const closeModal = () => setIsModalOpen(false);
  const onModalSuccess = () => {
    setIsModalOpen(false);
    fetchStyles();
  };

  const renderContent = () => {
    if (loading) return <LoadingMessage>Loading styles...</LoadingMessage>;
    if (error) return <ErrorMessage>{error}</ErrorMessage>;

    return (
      <>
        <ActionButton onClick={addStyle}>Add Style</ActionButton>
        <Table>
          <thead>
            <Tr>
              <Th>Edit</Th>
              <Th>Name</Th>
              <Th>Avg Sentences</Th>
              <Th>Excl. Frequency</Th>
              <Th>Jargon ID</Th>
            </Tr>
          </thead>
          <tbody>
            {styles.map(style => (
              <Tr key={style.id}>
                <Td>
                  <IconButton onClick={() => editStyle(style.id)}><i className="fa-sharp fa-solid fa-pen-to-square"></i></IconButton>
                  <IconButton onClick={() => deleteStyle(style.id, style.name)}><i className="fa-sharp fa-solid fa-trash"></i></IconButton>
                </Td>
                <Td>{style.name}</Td>
                <Td>{style.averageSentencesPerParagraph}</Td>
                <Td>{style.exclamationPointFrequency}</Td>
                <Td>{style.styleJargonId}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </>
    );
  };

  return (
    <ListContainer>
      <h1>Styles</h1>
      {renderContent()}
      <Modal isOpen={isModalOpen} onClose={closeModal}>
        <StyleForm onCancel={closeModal} onSuccess={onModalSuccess} initialStyle={selectedStyle} />
      </Modal>
    </ListContainer>
  );
};

export default StyleList;
